import { useEffect, useRef, useState } from 'react';
import { CONFIRM_LOCK_FALLBACK_MS, createConfirmLock } from '../components/ui/confirmLockModel';

/**
 * DQ-126 / DQ-175 (the "double click" family) — the confirm button of a dialog locks at
 * the click itself (`mutation.isPending` only turns true on a later render).
 *
 *   const confirmOnce = useConfirmLock(loading);
 *   <button onClick={() => confirmOnce(onConfirm)} />
 *
 * Opens again when `loading` went true and back to false (a failed action can be retried),
 * or after CONFIRM_LOCK_FALLBACK_MS when the caller never reports `loading`.
 */
export function useConfirmLock(loading: boolean, open = true): (run: () => void) => void {
  const [lock] = useState(createConfirmLock);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { lock.loadingChanged(loading); }, [lock, loading]);
  useEffect(() => { if (!open) lock.reset(); }, [lock, open]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return (run) => {
    if (!lock.enter(loading)) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => lock.fallback(), CONFIRM_LOCK_FALLBACK_MS);
    run();
  };
}
